<template>
  <h1>Customer Analytics</h1>
  
  <div class="content-section filters">

    <div class="alert alert-warning" role="alert">After setting filters or changing sorting order, click "Apply filters"
    to re-query the database</div>
    <div class="row">
      <div class="col-md-4">
        <FormKit
            type="select"
            label="Customer type"
            :options="customerTypes"
            v-model="selectedCustomerType"
        ></FormKit>
      </div>

      <div class="col-md-4">
        <FormKit
            type="select"
            label="Sales contact"
            :options="salesContacts"
            v-model="selectedSalesID"
        ></FormKit>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <FormKit
            type="select"
            label="Filter Option"
            :options="filterDateOptions"
            v-model="selectedFilterDateOptions"
        ></FormKit>
      </div>

      <div class="col-md-4" v-if="selectedFilterDateOptions != ''">
        <FormKit
            type="datepicker"
            label="Date Start"
            v-model="selectedStartDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>
      <div class="col-md-4" v-if="selectedFilterDateOptions != ''">
        <FormKit
            type="datepicker"
            label="Date End"
            v-model="selectedEndDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

    </div>

    <div class="action-bar">
      <button @click="clearFilter" id="clearFilter">Clear filter</button>&#160;
      <button @click="exportZip" id="exportZip">Export Customers</button>&#160;
      <button @click="applyFilter" id="applyFilter1">Apply filter</button>
    </div>

    <div v-if="isListing">


      <easy-data-table
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :headers="headers"
          :items="customers"
          :search-field="searchField"
          :search-value="searchValue"
          :loading="loading"
          alternating
          border-cell
          buttons-pagination
          multi-sort
      >

        <template #header-customerid="header">
          <div class="customize-header">
            {{ header.text }}<br />
            <input v-if="header.value !== 'operations'" type="text" :id="'filter-' + header.value"
                   @click.stop style="width: 40px;"/>
          </div>
        </template>

        <template #header="header">
          <div class="customize-header">

            {{ header.text }}<br />

            <input v-if="header.value !== 'operations'" type="text" :id="'filter-' + header.value" @click.stop />
            <button v-else @click="applyFilter" id="applyFilter2">Apply filter</button>
          </div>
        </template>


        <template #item-customerid="item">
          <router-link :to="{ name: 'ViewCustomer', params: { id: item.customerid } }">
            <div style="text-align: right;">{{ item.customerid }}</div>
          </router-link>
        </template>

        <template #item-operations="item">
          <div class="operation-wrapper">
            <router-link
                :to="{
                name: 'ViewCustomer',
                params: {
                  id: item.customerid
                }
              }">

              <font-awesome-icon
                  icon="edit"
                  size="1x"
                  class="operation-icon"
                  title="View"
              >
              </font-awesome-icon>
            </router-link>
          </div>
        </template>

      </easy-data-table>
    </div>
  </div>
</template>


<script setup>
import {ref, onMounted, watch, computed} from "vue";
import CustomerService from "@/services/CustomerService";

const customers =  ref([]);
const searchField = ref("garmenttype");
const searchValue = ref("");
const customerTypes = ref([]);
const salesContacts = ref([]);

const selectedFilterDateOptions = ref("");
const selectedStartDate = ref('');
const selectedEndDate = ref('');
const datepickerClearedKey = ref(0);

const selectedCustomerType = ref('');
const selectedSalesID = ref('');

const filterDateOptions = ref({
  none: '',
  sign_up: 'Sign Up',
  first_order: 'First order',
  last_order: 'Last order'
});

const headers = [
  { text: "ID", value: "customerid", width: 20, sortable: false, align: "right"  },
  { text: "First name", value: "firstname", sortable: false  },
  { text: "Surname", value: "surname", sortable: false  },
  { text: "Mobile", value: "mobile", sortable: false  },
  { text: "Postcode", value: "postcode", sortable: false  },
  { text: "Account Code", value: "accountcode", sortable: false  },
  { text: "Location", value: "locationname", sortable: false  },
  { text: "Num Orders", value: "num_orders", sortable: false  },
  { text: "Signup", value: "datesignup", sortable: false  },

  { text: "Actions", value: "operations" },
];

const forceRenderDatepicker = () => {
  datepickerClearedKey.value += 1;
};

const firstnameCriteria = ref('');

const filterOptions = ref({});

const isListing = ref(true);

const loading = ref(false);
const serverItemsLength = ref(0);
const serverOptions = ref({
  page: 1,
  rowsPerPage: 25,
  //sortBy: ["customerid"],
  sortType: ["asc"],
  exportFormat: "none",
});

const  createDateAsUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
}

const loadFromServer = async () => {
  loading.value = true;
  const {
    serverCurrentPageItems,
    serverTotalItemsLength,
  } = await CustomerService.searchCustomers(serverOptions.value);
  customers.value = serverCurrentPageItems;
  serverItemsLength.value = serverTotalItemsLength;
  loading.value = false;
};


const loadCustomerTypes = async () => {

  const cTypes = await CustomerService.getCustomerTypes(serverOptions.value);
  let initList = [{label: "-- no customer type filter --", value: 0}]

  const mappedCTypes = cTypes.types.map((item) => ({label: item.label, value: item.id}))
  customerTypes.value = initList.concat(mappedCTypes);
};

const loadSalesContacts = async () => {

  const result = await CustomerService.getSalesContacts();
  let initList = [{label: "-- no sales contact filter --", value: 0}]

  const mappedRes = result.sales_contacts.map((item) => ({label: item.userlabel, value: item.userid}))
  salesContacts.value = initList.concat(mappedRes);
};

const applyFilter = (resetPage) => {
  let filters = {};
  let updateOptions = serverOptions.value;
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = document.getElementById("filter-" + filterProperty).value;
    }
  }
  updateOptions.filters = filters;

  if (selectedCustomerType.value !== "") {
    updateOptions.customerType = selectedCustomerType.value;
  }

  if (selectedSalesID.value !== "") {
    updateOptions.salesID = selectedSalesID.value;
  }

  if (selectedFilterDateOptions.value !== "" && selectedFilterDateOptions.value !== "none" && typeof selectedFilterDateOptions.value != "undefined") {
    updateOptions.filter_option = selectedFilterDateOptions.value;
  }
  if (selectedStartDate.value !== "" && typeof selectedStartDate.value != "undefined") {
    selectedStartDate.value = createDateAsUTC(new Date(selectedStartDate.value));
    updateOptions.start_date = selectedStartDate.value;
  }
  if (selectedEndDate.value !== "" && typeof selectedEndDate.value != "undefined") {
    selectedEndDate.value = createDateAsUTC(new Date(selectedEndDate.value));
    updateOptions.end_date = selectedEndDate.value;
  }
  if (resetPage) {
    updateOptions.page = 1;
  }

  serverOptions.value = updateOptions;

  loadFromServer();
};

const exportZip = async () => {
  loading.value = true;
  await CustomerService.downloadCustomers(serverOptions.value);
  loading.value = false;
};

const clearFilter = () => {
  let filters = {};
  for (let i = 0; i < headers.length; i++) {
    const filterProperty = headers[i].value;
    if (filterProperty !== "operations") {
      filters[headers[i].value] = "";
      document.getElementById("filter-" + filterProperty).value = "";
    }
  }

  const updateServerOptions = serverOptions.value;
  updateServerOptions.filters = filters;
  updateServerOptions.customerType = "";
  updateServerOptions.salesID = "";
  updateServerOptions.filter_option = "";
  updateServerOptions.start_date = "";
  updateServerOptions.end_date = "";

  selectedCustomerType.value = "";
  selectedSalesID.value = "";
  selectedFilterDateOptions.value = "";
  selectedStartDate.value = "";
  selectedEndDate.value = "";
  serverOptions.value = updateServerOptions;

  forceRenderDatepicker();

  loadFromServer();
};

// initial load
loadFromServer();


//watch(serverOptions, (value) => { loadFromServer(); }, { deep: true });


watch(() => serverOptions.value.page, () => {
  applyFilter(false)
});

onMounted(() => {
    loadCustomerTypes();
    loadSalesContacts();
});
</script>
